'use client';

import React, { useMemo, memo, useCallback } from 'react';
import { motion } from 'motion/react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { useTheme } from "@/providers/ThemeProvider";
import Image from 'next/image';
import { useStepGradients, animations } from './theme-utils';
import { useTranslations } from 'next-intl';

// Pre-computed animation objects to avoid recreation
const IMAGE_ANIMATION = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { delay: 0.3, duration: 0.5, type: "spring" }
  }
};

const CONTENT_BOX_ANIMATION = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.5, duration: 0.4 }
  }
};

const CONTENT_TEXT_ANIMATION = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { delay: 0.7, duration: 0.3 }
  }
};

const BUTTON_ANIMATION = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { delay: 0.9, duration: 0.3 }
  }
};

// Memoized Button Components for better performance
const SkipButton = memo(({ isDarkMode, onClick }) => (
  <motion.button
    variants={animations.content.standard}
    whileHover={animations.button.hover}
    whileTap={animations.button.tap}
    onClick={onClick}
    className={`
      p-1.5 rounded-full transition-colors flex-shrink-0
      ${isDarkMode
        ? 'bg-gray-700/50 hover:bg-gray-700 text-gray-300'
        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}
    `}
    aria-label="Skip"
    type="button"
  >
    <X size={18} />
  </motion.button>
));
SkipButton.displayName = 'SkipButton';

const PrevButton = memo(({ isDarkMode, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.05, x: -2 }}
    whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`
      p-2 rounded-full flex items-center justify-center transition-colors
      ${isDarkMode
        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}
    `}
    type="button"
    aria-label="Back"
  >
    <ChevronLeft size={18} />
  </motion.button>
));
PrevButton.displayName = 'PrevButton';

const NextButton = memo(({ buttonGradient, isDarkMode, onClick, isLastStep, buttonText }) => (
  <motion.button
    whileHover={{ scale: 1.05, x: 2 }}
    whileTap={{ scale: 0.95 }}
    {...(isLastStep ? animations.button.pulse : {})}
    onClick={onClick}
    className={`
      p-2 rounded-full flex items-center justify-center
      ${buttonGradient} shadow-md
      ${isDarkMode ? 'text-white' : 'text-gray-800'}
      transition-all
    `}
    type="button"
    aria-label={buttonText}
  >
    <ChevronRight size={18} />
  </motion.button>
));
NextButton.displayName = 'NextButton';

// Memoized Icon Display component
const IconDisplay = memo(({ icon, iconGradient }) => (
  <motion.div
    initial={{ scale: 0.8, opacity: 0 }}
    animate={{
      scale: 1,
      opacity: 1,
      transition: { delay: 0.2, duration: 0.4, type: "spring" }
    }}
    className={`
      flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0
      ${iconGradient}
    `}
  >
    {typeof icon === 'string'
      ? <span className="text-xl">{icon}</span>
      : icon
    }
  </motion.div>
));
IconDisplay.displayName = 'IconDisplay';

// Custom scrollbar styles as a constant to prevent recreation
const getScrollbarStyles = (isDarkMode) => `
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: ${isDarkMode ? '#374151' : '#f1f1f1'};
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: ${isDarkMode ? '#4B5563' : '#cbd5e0'};
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: ${isDarkMode ? '#6B7280' : '#a0aec0'};
  }
`;

// Enhanced animated tour card with themed gradients and dynamic content
const DynamicTourCard = memo(({
  step,
  currentStep,
  totalSteps,
  nextStep,
  prevStep,
  skipTour,
  arrow,
}) => {
  const { isDarkMode } = useTheme();
  const { getStepGradient } = useStepGradients();
  const t = useTranslations("Tour");

  // Memoize these values to prevent recalculation
  const isLastStep = useMemo(() => currentStep === totalSteps - 1, [currentStep, totalSteps]);
  const isFinalStep = useMemo(() => !step.selector && !step.isIntroStep, [step]);

  // Button event handlers with more aggressive event prevention
  const handleNextClick = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (nextStep) nextStep();
  }, [nextStep]);

  const handlePrevClick = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (prevStep) prevStep();
  }, [prevStep]);

  const handleSkipClick = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    if (skipTour) skipTour();
  }, [skipTour]);

  // Pre-calculate common values - memoized for performance
  const buttonGradient = useMemo(() => getStepGradient('button', currentStep), [getStepGradient, currentStep]);
  const cardGradient = useMemo(() => getStepGradient('card', currentStep), [getStepGradient, currentStep]);
  const iconGradient = useMemo(() => getStepGradient('icon', currentStep), [getStepGradient, currentStep]);

  // Memoize scrollbar styles
  const scrollbarStyles = useMemo(() => getScrollbarStyles(isDarkMode), [isDarkMode]);

  // Determine which animation variant to use based on step type
  const cardAnimation = useMemo(() =>
    step.isIntroStep ? animations.card.intro : (isFinalStep ? animations.card.slide : animations.card.standard),
    [step.isIntroStep, isFinalStep]
  );

  // Determine button text based on step
  const buttonText = useMemo(() =>
    isLastStep ? t('buttons.finish', 'Finish') : t('buttons.next', 'Next'),
    [isLastStep, t]
  );

  // Coach introduction step (first step)
  if (step.isIntroStep) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={cardAnimation}
        className="z-[9999] flex items-end max-w-3xl"
      >
        <motion.div
          className="relative mb-8 mr-6"
          {...IMAGE_ANIMATION}
        >
          <div className="size-24 sm:size-32 md:size-60 relative">
            {step.coachImage && (
              <Image
                src={step.coachImage.src}
                alt={step.coachImage.alt}
                width={1000}
                height={1000}
                className=""
                priority={true} // Load priority for first step
              />
            )}
          </div>
        </motion.div>

        <motion.div
          className={`
            relative min-w-64 sm:min-w-80 max-w-md rounded-2xl p-6 shadow-xl
            ${isDarkMode ? 'bg-linear-to-br from-indigo-700 to-indigo-900' : 'bg-linear-to-br from-indigo-500 to-indigo-700'}
            text-white
          `}
          {...CONTENT_BOX_ANIMATION}
        >
          {/* Improved triangle pointer with better positioning */}
          <div className="absolute left-[-16px] bottom-10 w-0 h-0
            border-t-[12px] border-t-transparent
            border-r-[16px] border-r-indigo-600 dark:border-r-indigo-800
            border-b-[12px] border-b-transparent"
          />

          <motion.div
            className="mb-5 text-base sm:text-lg font-medium leading-relaxed"
            {...CONTENT_TEXT_ANIMATION}
          >
            {step.content}
          </motion.div>

          <motion.div
            className="flex justify-end mt-4"
            {...BUTTON_ANIMATION}
          >
            <motion.button
              whileHover={animations.button.hover}
              whileTap={animations.button.tap}
              onClick={handleNextClick}
              className="px-5 py-2 rounded-lg text-base font-medium bg-white/20 hover:bg-white/30 text-white flex items-center gap-2 transition-colors shadow-md"
              type="button"
            >
              {t('buttons.start_tour', 'Start Tour')}
              <ChevronRight size={18} />
            </motion.button>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  // Final step without a specific target element
  if (isFinalStep) {
    return (
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={animations.card.slide}
        className={`
          rounded-xl shadow-lg p-6 backdrop-blur-sm z-[9999] max-w-[90vw] w-[320px] sm:w-[400px]
          bg-linear-to-br from-emerald-500 to-teal-600 text-white
        `}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <motion.div
              variants={animations.content.standard}
              className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 text-white"
            >
              {step.icon}
            </motion.div>

            <motion.h3
              variants={animations.content.standard}
              className="text-xl font-bold"
            >
              {step.title}
            </motion.h3>
          </div>
        </div>

        <motion.div
          variants={animations.content.standard}
          className="mb-6 leading-relaxed"
        >
          {step.content}
        </motion.div>

        <motion.div
          variants={animations.content.standard}
          className="flex justify-center"
        >
          <motion.button
            whileHover={animations.button.hover}
            whileTap={animations.button.tap}
            animate={animations.button.pulse.animate}
            onClick={handleNextClick}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-white text-emerald-600 shadow-lg flex items-center gap-1"
            type="button"
          >
            {t('buttons.finish', 'Finish')}
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  // Standard tour step card with dynamic styling
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={cardAnimation}
      className={`
        rounded-xl shadow-lg p-5 backdrop-blur-sm z-[9999] max-w-[90vw] w-[360px] sm:w-[400px]
        ${cardGradient} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
        ${isDarkMode ? 'text-white' : 'text-gray-800'}
      `}
    >
      {/* Header with icon and title */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <IconDisplay icon={step.icon} iconGradient={iconGradient} />

          <motion.h3
            variants={animations.content.standard}
            className="text-lg font-bold leading-tight"
          >
            {step.title}
          </motion.h3>
        </div>

        {step.showSkip && (
          <SkipButton isDarkMode={isDarkMode} onClick={handleSkipClick} />
        )}
      </div>

      {/* Main content - scrollable */}
      <motion.div
        variants={animations.content.standard}
        className="mb-5 text-sm leading-relaxed overflow-y-auto max-h-[150px] pr-1 custom-scrollbar"
      >
        {step.content}
      </motion.div>

      {/* Arrow indicator */}
      {arrow}

      {/* Navigation controls - redesigned layout */}
      <motion.div
        variants={animations.content.standard}
        className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-center justify-between">
          {currentStep > 0 ? (
            <PrevButton isDarkMode={isDarkMode} onClick={handlePrevClick} />
          ) : (
            <div className="w-8"></div>
          )}

          <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
            {currentStep + 1} / {totalSteps}
          </div>

          <NextButton
            buttonGradient={buttonGradient}
            isDarkMode={isDarkMode}
            onClick={handleNextClick}
            isLastStep={isLastStep}
            buttonText={buttonText}
          />
        </div>
      </motion.div>

      {/* Custom scrollbar styles */}
      <style jsx global>{scrollbarStyles}</style>
    </motion.div>
  );
});

DynamicTourCard.displayName = 'DynamicTourCard';

export default DynamicTourCard;